import { cn } from '@listening/utils/react-helpers';
import { Slider } from '@listening/frontend/components/radix/slider';
import { useState } from 'react';

// All speed options
const allSpeedOptions: Array<string> = [
  '0.75',
  '1.0',
  '1.10',
  '1.15',
  '1.25',
  '1.5',
  '1.75',
  '1.85',
  '2.5',
];

// For smallest screens: first row (3 options)
const xsScreenFirstRow = allSpeedOptions.slice(0, 3);
// For smallest screens: second row (3 options)
const xsScreenSecondRow = allSpeedOptions.slice(3, 6);
// For smallest screens: third row (3 options)
const xsScreenThirdRow = allSpeedOptions.slice(6);

// For medium screens: first row (5 options)
const mdScreenFirstRow = allSpeedOptions.slice(0, 5);
// For medium screens: second row (4 options)
const mdScreenSecondRow = allSpeedOptions.slice(5);

// For large screens: first row (7 options)
const lgScreenFirstRow = allSpeedOptions.slice(0, 7);
// For large screens: second row (2 options)
const lgScreenSecondRow = allSpeedOptions.slice(7);

function SpeedButton({
  speed,
  selectedSpeed,
  onClick,
  className,
}: {
  speed: string;
  selectedSpeed: string;
  onClick: (speed: string) => void;
  className?: string | undefined;
}) {
  return (
    <button
      aria-label={`Set speed to ${speed}x`}
      aria-pressed={speed === selectedSpeed}
      className={cn(
        'rounded-lg font-medium transition-colors',
        'text-gray-800',
        {
          'bg-blue-100': speed === selectedSpeed,
          'hover:bg-gray-100': speed !== selectedSpeed,
        },
        className,
      )}
      key={speed}
      onClick={() => {
        onClick(speed);
      }}
      type='button'
    >
      {speed}x
    </button>
  );
}

function SpeedOptionsRow({
  speeds,
  selectedSpeed,
  handleSpeedClick,
  className,
  buttonClassName,
}: {
  speeds: Array<string>;
  selectedSpeed: string;
  handleSpeedClick: (speed: string) => void;
  className: string;
  buttonClassName?: string;
}) {
  return (
    <div className={className} role='radiogroup'>
      {speeds.map((speed) => (
        <SpeedButton
          className={buttonClassName}
          key={speed}
          onClick={handleSpeedClick}
          selectedSpeed={selectedSpeed}
          speed={speed}
        />
      ))}
    </div>
  );
}

export default function SpeedControlFeatures() {
  const defaultSpeedIndex = allSpeedOptions.indexOf('1.25');
  const [selectedSpeed, setSelectedSpeed] = useState('1.25');
  const [sliderValue, setSliderValue] = useState([
    (defaultSpeedIndex / (allSpeedOptions.length - 1)) * 100,
  ]);

  const handleSpeedClick = (speed: string): void => {
    setSelectedSpeed(speed);
    // Calculate slider position based on speed
    const index = allSpeedOptions.indexOf(speed);
    const newSliderValue = [(index / (allSpeedOptions.length - 1)) * 100];
    setSliderValue(newSliderValue);
  };

  const handleSliderChange = (value: Array<number>): void => {
    if (value[0] === undefined) {
      return;
    }
    // Calculate speed based on slider position
    const index = Math.round((value[0] / 100) * (allSpeedOptions.length - 1));
    const newSpeed = allSpeedOptions[index];
    if (newSpeed) {
      setSelectedSpeed(newSpeed);
      setSliderValue(value);
    }
  };

  return (
    <div className='relative flex justify-center lg:justify-start'>
      <div
        className='w-full max-w-2xl rounded-3xl p-8 shadow-lg'
        style={{
          backgroundColor: 'rgba(6, 135, 251, 0.05)',
        }}
      >
        <div className='mb-8 flex items-center gap-4'>
          <div className='rounded-full p-2 text-blue-600'>
            <svg
              fill='none'
              height='28'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              width='28'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle cx='12' cy='12' r='10' />
              <polyline points='12 6 12 12 16 14' />
            </svg>
          </div>
          <span className='text-2xl font-semibold'>Speed</span>
        </div>

        <div className='flex flex-col gap-6'>
          <div className='flex items-center justify-center gap-4'>
            <button
              aria-label='Decrease speed'
              className='flex size-10 flex-shrink-0 items-center justify-center pb-2 text-gray-800 transition-all duration-200 hover:scale-110 hover:text-blue-600 focus:outline-none active:scale-95'
              onClick={() => {
                const currentIndex = allSpeedOptions.indexOf(selectedSpeed);
                if (currentIndex > 0) {
                  const newSpeed = allSpeedOptions[currentIndex - 1];
                  if (newSpeed) {
                    handleSpeedClick(newSpeed);
                  }
                }
              }}
              type='button'
            >
              <span
                aria-hidden='true'
                className='text-3xl leading-none font-medium'
              >
                −
              </span>
            </button>

            <div className='relative w-full max-w-md py-2'>
              <Slider
                aria-label='Speed control slider'
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={sliderValue[0]}
                className='w-full'
                max={100}
                onValueChange={handleSliderChange}
                rangeClassName='bg-blue-500 h-full rounded-full'
                step={1}
                thumbClassName='border-white border-2 bg-blue-500 cursor-pointer w-5 h-5 rounded-full shadow-md hover:scale-110 transition-transform duration-200'
                trackClassName='bg-gray-200 cursor-pointer h-2 rounded-full'
                value={sliderValue}
              />
            </div>

            <button
              aria-label='Increase speed'
              className='flex size-10 flex-shrink-0 items-center justify-center pb-2 text-gray-800 transition-all duration-200 hover:scale-110 hover:text-blue-600 focus:outline-none active:scale-95'
              onClick={() => {
                const currentIndex = allSpeedOptions.indexOf(selectedSpeed);
                if (currentIndex < allSpeedOptions.length - 1) {
                  const newSpeed = allSpeedOptions[currentIndex + 1];
                  if (newSpeed) {
                    handleSpeedClick(newSpeed);
                  }
                }
              }}
              type='button'
            >
              <span
                aria-hidden='true'
                className='text-3xl leading-none font-medium'
              >
                +
              </span>
            </button>
          </div>

          <div className='flex flex-col gap-2 sm:hidden'>
            <SpeedOptionsRow
              buttonClassName='px-1 py-2 text-sm'
              className='grid grid-cols-3 gap-2'
              handleSpeedClick={handleSpeedClick}
              selectedSpeed={selectedSpeed}
              speeds={xsScreenFirstRow}
            />

            <SpeedOptionsRow
              buttonClassName='px-1 py-2 text-sm'
              className='grid grid-cols-3 gap-2'
              handleSpeedClick={handleSpeedClick}
              selectedSpeed={selectedSpeed}
              speeds={xsScreenSecondRow}
            />

            <SpeedOptionsRow
              buttonClassName='px-1 py-2 text-sm'
              className='grid grid-cols-3 gap-2'
              handleSpeedClick={handleSpeedClick}
              selectedSpeed={selectedSpeed}
              speeds={xsScreenThirdRow}
            />
          </div>

          <div className='hidden sm:flex sm:flex-col sm:gap-4 lg:hidden'>
            <SpeedOptionsRow
              buttonClassName='px-2 py-2 text-base'
              className='grid grid-cols-5 gap-2'
              handleSpeedClick={handleSpeedClick}
              selectedSpeed={selectedSpeed}
              speeds={mdScreenFirstRow}
            />

            <SpeedOptionsRow
              buttonClassName='px-2 py-2 text-base'
              className='grid grid-cols-4 gap-2'
              handleSpeedClick={handleSpeedClick}
              selectedSpeed={selectedSpeed}
              speeds={mdScreenSecondRow}
            />
          </div>

          <div className='hidden lg:flex lg:flex-col lg:gap-6'>
            <SpeedOptionsRow
              buttonClassName='px-2 py-3 text-base'
              className='grid grid-cols-7 gap-3'
              handleSpeedClick={handleSpeedClick}
              selectedSpeed={selectedSpeed}
              speeds={lgScreenFirstRow}
            />

            <SpeedOptionsRow
              buttonClassName='px-2 py-3 text-base'
              className='mx-auto grid max-w-[calc(2/7*100%)] grid-cols-2 gap-2.5'
              handleSpeedClick={handleSpeedClick}
              selectedSpeed={selectedSpeed}
              speeds={lgScreenSecondRow}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
